import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

import {map,tap} from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
@Injectable({
  providedIn: 'root'
})
export class AuthInterceptorService implements HttpInterceptor {

  constructor(
    private router: Router,
    public _auth: AuthService,

  ) {}




  // Ventana de emergencia del fallback. El backend renueva con 120 s de margen;
  // acá se usa la mitad para que el fallback solo entre si el encabezado de
  // verdad no llegó, y no compita con él.
  private static readonly FALLBACK_WINDOW_S = 60;

  /**
   * Guarda el token renovado que viene en el encabezado de la respuesta.
   *
   * El backend (validarJWT) lo emite SOLO cuando al token le quedan menos de
   * 2 minutos de vida, así que esto casi nunca hace nada — y ahí está la ganancia:
   * antes se disparaba un POST /api/auth/refresh-token después de CADA respuesta.
   */
  private guardarTokenRenovado = (res: HttpResponse<any>, skipRefresh: boolean) => {
    if (skipRefresh) return;
    if (this._auth.getIsLoggingOut()) return;
    if (!localStorage.getItem('x-token')) return;   // estamos en logout

    // El encabezado solo es legible si el backend manda
    // Access-Control-Expose-Headers: X-Refreshed-Token. Si acá siempre da null,
    // eso es lo primero que hay que revisar del lado del servidor.
    const nuevo = res.headers.get('X-Refreshed-Token');
    if (nuevo) {
      localStorage.setItem('x-token', nuevo);
      return;
    }

    // Compatibilidad: si algún endpoint devuelve el token en el cuerpo, sirve igual.
    if (res.body?.x_token) {
      localStorage.setItem('x-token', res.body.x_token);
    }
  }

  /**
   * Red de seguridad para el despliegue: si el backend todavía no emite el
   * encabezado y el token está por vencerse, se pide el refresh a la vieja usanza.
   * Se puede borrar —junto con AuthService.refresh_token— cuando el backend con
   * sliding token esté en todos los ambientes.
   */
  private refrescarSiEstaPorVencer = async () => {
    try {
      const token = localStorage.getItem('x-token');
      if (!token || this._auth.getIsLoggingOut()) return;

      const segundosRestantes = this.segundosParaVencer(token);
      if (segundosRestantes === null) return;
      if (segundosRestantes > AuthInterceptorService.FALLBACK_WINDOW_S) return;

      const resp = await this._auth.refresh_token({ 'x_token': token });
      if (resp?.x_token) {
        console.log('🔁 INTERCEPTOR: fallback de refresh usado — el backend no emitió el encabezado');
        localStorage.setItem('x-token', resp.x_token);
      }
    } catch (error: any) {
      console.log('🔴 INTERCEPTOR: Error en el fallback de refresh:', error);
    }
  }

  /** Segundos que le quedan al JWT, leídos del payload. null si no se puede leer. */
  private segundosParaVencer = (token: string): number | null => {
    try {
      const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      if (!payload?.exp) return null;
      return payload.exp - Math.floor(Date.now() / 1000);
    } catch {
      return null;
    }
  }
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token: any = localStorage.getItem('x-token');
    let request = req;

    // No agregar token automático si es una llamada del simulador
    const skipAuthInterceptor = req.headers.get('X-Skip-Auth-Interceptor');
    
    // Verificar si la request ya tiene authorization custom (para setup 2FA sin localStorage)
    const hasCustomAuth = req.headers.has('authorization');

    if (token && !skipAuthInterceptor && !hasCustomAuth) {
      // Solo agregar authorization si NO hay uno custom
      request = req.clone({
        setHeaders: {
        // authorization: `Bearer ${ token }`
           authorization: `${ token }`,

           // Opt-in del sliding token: le dice al backend que esta respuesta SÍ
           // se le va a leer el encabezado X-Refreshed-Token. Sin esto el backend
           // no firma nada, y seguiríamos dependiendo del refresh-token aparte.
           'X-Sliding-Token': '1'

        }
      });
    } else if (hasCustomAuth && !token) {
      // Si ya tiene authorization custom y NO hay token en localStorage, dejar pasar
      console.log('🔓 Interceptor: Request ya tiene authorization custom, no modificando headers');
      request = req;
    }

    return next.handle(request)
    .pipe(map((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        // Verificar si debemos saltar el refresh
        const skipRefresh = request.headers.get('X-Skip-Token-Refresh') === 'true';

        // El token renovado viene en el encabezado de esta misma respuesta.
        // Ya NO se dispara una llamada aparte por cada respuesta.
        this.guardarTokenRenovado(event, skipRefresh);

        if (!skipRefresh) {
          // Fallback: solo si el token está a menos de 60 s y el backend no
          // mandó el encabezado. Se quita cuando el sliding token esté en todos
          // los ambientes.
          this.refrescarSiEstaPorVencer();
        }
      }
      return event;
  })).pipe(
    catchError((resp: HttpErrorResponse) => {
      // No cerrar sesión si es una llamada del simulador
      const skipAuthInterceptor = request.headers.get('X-Skip-Auth-Interceptor');
      // Una consulta de fondo (p. ej. el contador del menú, FIR-03) no cierra la sesión: si la sesión venció de verdad,
      // la primera llamada de la pantalla la cierra igual (Mario, 14-oct: el contador sacaba al tesorero de la consola).
      const sinCerrarSesion = request.headers.get('X-Sin-Cerrar-Sesion') === 'true';
      
      if (resp.status === 401 && !skipAuthInterceptor && !sinCerrarSesion) {
        // Solo hacer logout si el 401 viene de NUESTRO backend, no de APIs externas
        const isOwnBackend = request.url.startsWith(environment.BASE_LA);
        
        if (isOwnBackend) {
          console.log('🔴 INTERCEPTOR: Error 401 del backend propio, cerrando sesión. URL:', request.url);
          
          // Verificar si la sesión fue invalidada (expirada o cerrada desde otro dispositivo)
          if (resp.error?.code === 'SESSION_INVALIDATED' || resp.error?.sessionClosed) {
            sessionStorage.setItem('sessionClosedMessage', resp.error?.msg || 'Tu sesión ha expirado o fue cerrada desde otro dispositivo');
          }
          
          this.router.navigate([environment.logoutRoute], {});
        } else {
          console.log('🔴 INTERCEPTOR: Error 401 de API externa (ignorado para logout). URL:', request.url);
        }
      }

      return throwError(() => resp);

    })
  );



/*


    return next.handle(request).pipe(
      catchError((resp: HttpErrorResponse) => {
        if (resp.status === 401) {
          this.router.navigate([environment.logoutRoute], {});
        }

        return throwError(() => resp);

      })
    );*/
  }

}
